import { Icon, type IconProps } from '../Icon';
import {
  listItemVariants,
  markerColorTextClass,
  resolveArrowDirection,
  type ArrowDirection,
  type BulletStyle,
  type ListMarkerColor,
} from './list.variants';
import { cn } from '@/hen-ui/utils';

type IconMarkerColor = NonNullable<IconProps['color']>;

/** `body` is not an Icon color. The glyph inherits `text-body` from its wrapper. */
function resolveMarkerIconColor(
  markerColor: ListMarkerColor | undefined,
  fallback?: IconMarkerColor,
): IconMarkerColor | undefined {
  if (markerColor === 'body') {
    return 'default';
  }
  return markerColor ?? fallback;
}

function markerFrameClassName(markerColor?: ListMarkerColor): string {
  return cn(
    'flex h-text-line shrink-0 items-center',
    markerColor === 'body' && markerColorTextClass.body,
  );
}

interface ListItemMarkerProps {
  bulletStyle: BulletStyle;
  arrowDirection?: ArrowDirection | string;
  itemIndex?: number;
  markerColor?: ListMarkerColor;
}

function ArrowTriangleMarker({
  pointsUp,
  markerColor,
}: {
  pointsUp: boolean;
  markerColor?: ListMarkerColor;
}) {
  return (
    <span className={markerFrameClassName(markerColor)} aria-hidden>
      <Icon
        icon='triangle'
        fill='currentColor'
        stroke='none'
        strokeWidth={0}
        color={resolveMarkerIconColor(markerColor)}
        className={cn(
          'size-3 fill-current stroke-none translate-y-px',
          !pointsUp && 'rotate-180',
          // Directional pros/cons colors apply only when no explicit override.
          !markerColor && (pointsUp ? 'text-success-500' : 'text-error-500'),
        )}
      />
    </span>
  );
}

const markerTextClassName = cn(listItemVariants(), 'shrink-0 tabular-nums');

export function ListItemMarker({
  bulletStyle,
  arrowDirection = 'up',
  itemIndex = 0,
  markerColor,
}: ListItemMarkerProps) {
  if (bulletStyle === 'check') {
    return (
      <span className={markerFrameClassName(markerColor)} aria-hidden>
        <Icon
          icon='circle-check'
          size='sm'
          color={resolveMarkerIconColor(markerColor, 'success')}
          className='translate-y-px'
        />
      </span>
    );
  }

  if (bulletStyle === 'x') {
    return (
      <span className={markerFrameClassName(markerColor)} aria-hidden>
        <Icon
          icon='circle-x'
          size='sm'
          color={resolveMarkerIconColor(markerColor, 'error')}
          className='translate-y-px'
        />
      </span>
    );
  }

  if (bulletStyle === 'number') {
    return (
      <span
        className={cn(
          'flex h-text-line shrink-0 items-center',
          markerTextClassName,
          markerColor && markerColorTextClass[markerColor],
        )}
        aria-hidden
      >
        {itemIndex + 1}.
      </span>
    );
  }

  if (bulletStyle === 'q') {
    return (
      <span
        className={cn(
          'flex h-text-line shrink-0 items-center font-bold',
          markerTextClassName,
          markerColor && markerColorTextClass[markerColor],
        )}
        aria-hidden
      >
        Q.
      </span>
    );
  }

  if (bulletStyle === 'arrows') {
    return (
      <ArrowTriangleMarker
        pointsUp={resolveArrowDirection(arrowDirection) === 'up'}
        markerColor={markerColor}
      />
    );
  }

  return null;
}
